import type { SigninReply } from 'definitions';
import { Trash2 } from 'lucide-react';
import { SIGNIN_ANNOUNCEMENT_LIMITS as LIMITS } from 'definitions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { switchReplyKind } from './announcement-form';

const FIELD_INPUT = 'border-line bg-field text-foreground h-10 rounded-lg text-body';
const FIELD_TEXTAREA = 'border-line bg-field text-foreground rounded-lg text-body';

type ReplyEditorProps = {
  reply: SigninReply;
  position: number;
  onChange: (reply: SigninReply) => void;
  onRemove: () => void;
};

const KINDS: readonly { kind: SigninReply['kind']; label: string }[] = [
  { kind: 'text', label: 'Text answer' },
  { kind: 'link', label: 'Link card' },
];

/**
 * One suggested question and what Pertento answers. The answer is either a text
 * bubble or a link card, never both: a visitor who taps "Show me" gets one thing.
 */
export const ReplyEditor = ({ reply, position, onChange, onRemove }: ReplyEditorProps) => {
  const idBase = `signin-reply-${position}`;

  return (
    <div className="border-line bg-panel2 flex flex-col gap-3 rounded-card border p-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="micro-label">Question {position}</span>
        <div className="flex items-center gap-1.5">
          {KINDS.map(({ kind, label }) => (
            <Button
              key={kind}
              type="button"
              size="sm"
              variant={reply.kind === kind ? 'default' : 'outline'}
              className="h-8 rounded-lg"
              onClick={() => onChange(switchReplyKind({ reply, kind }))}
            >
              {label}
            </Button>
          ))}
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="h-8 w-8 rounded-lg"
            aria-label={`Remove question ${position}`}
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={`${idBase}-question`} className="micro-label block">
          What the visitor taps
        </label>
        <Input
          id={`${idBase}-question`}
          value={reply.question}
          maxLength={LIMITS.question}
          placeholder="How do I set one up?"
          onChange={(ev) => onChange({ ...reply, question: ev.target.value })}
          className={FIELD_INPUT}
        />
      </div>

      {reply.kind === 'text' ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${idBase}-text`} className="micro-label block">
            Answer
          </label>
          <Textarea
            id={`${idBase}-text`}
            value={reply.text}
            maxLength={LIMITS.answerText}
            rows={2}
            onChange={(ev) => onChange({ ...reply, text: ev.target.value })}
            className={FIELD_TEXTAREA}
          />
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${idBase}-label`} className="micro-label block">
              Card title
            </label>
            <Input
              id={`${idBase}-label`}
              value={reply.label}
              maxLength={LIMITS.linkLabel}
              placeholder="Set up a passkey"
              onChange={(ev) => onChange({ ...reply, label: ev.target.value })}
              className={FIELD_INPUT}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`${idBase}-url`} className="micro-label block">
              Link
            </label>
            <Input
              id={`${idBase}-url`}
              value={reply.url}
              maxLength={LIMITS.linkUrl}
              placeholder="https://www.pertento.ai/docs/passkeys"
              onChange={(ev) => onChange({ ...reply, url: ev.target.value })}
              className={FIELD_INPUT}
            />
          </div>
        </div>
      )}
    </div>
  );
};
